import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/motion";
import api, { BACKEND_URL } from "@/lib/api";
import { withParams } from "@/lib/track";

export default function OffersPage({ category, eyebrow, title, intro }) {
  const [offers, setOffers] = useState(null);

  useEffect(() => {
    api
      .get("/offers", { params: { category } })
      .then((r) => setOffers(r.data))
      .catch(() => setOffers([]));
  }, [category]);

  return (
    <div data-testid={`offers-page-${category}`}>
      <section className="px-6 pt-40 pb-20">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#0C6B4F] mb-8">
            <MaskedLine delay={0.1}>{eyebrow}</MaskedLine>
          </p>
          <h1 className="font-display font-light text-5xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl text-[#0F1F1A]">
            <MaskedLine delay={0.25}>{title}</MaskedLine>
          </h1>
          <MaskedLine delay={0.5} className="mt-8 max-w-xl">
            <span className="text-[#5C6B62] leading-relaxed">{intro}</span>
          </MaskedLine>
          <FadeUp delay={0.7}>
            <p className="mt-8 font-mono2 text-[11px] uppercase tracking-[0.15em] text-[#8A978D]" data-testid="offers-disclosure">
              Trusted Financial Hub may be compensated by providers listed here ·{" "}
              <Link to="/disclosures#advertising" className="underline hover:text-[#0C6B4F]">Advertising Disclosure</Link>
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="px-6 pb-32" data-testid="offers-list">
        <div className="max-w-7xl mx-auto">
          {offers === null && (
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#8A978D] py-16">Loading providers…</p>
          )}
          {offers !== null && offers.length === 0 && (
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#8A978D] py-16" data-testid="offers-empty">
              No options listed right now — check back soon.
            </p>
          )}
          {offers?.map((o, i) => (
            <FadeUp key={o.id} delay={i * 0.05} className={`border-t border-[#DFE5DC] last:border-b py-10 grid gap-8 items-center group ${o.image_url ? "md:grid-cols-[240px_1fr_auto]" : "md:grid-cols-[80px_1fr_auto]"}`}>
              {o.image_url ? (
                <div className="bg-white border border-[#DFE5DC] rounded-2xl aspect-[16/10] w-full md:w-[240px] flex items-center justify-center p-3 overflow-hidden group-hover:border-[#0C6B4F]/40 transition-colors duration-500" data-testid={`offer-banner-${o.slug}`}>
                  <img
                    src={o.image_url.startsWith("/") ? `${BACKEND_URL}${o.image_url}` : o.image_url}
                    alt={`${o.name} banner`}
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                  />
                </div>
              ) : (
                <span className="font-mono2 text-sm text-[#C9A227]">{String(i + 1).padStart(2, "0")}</span>
              )}
              <div>
                {o.badge && (
                  <span className="inline-block font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#0C6B4F] border border-[#0C6B4F]/40 rounded-full px-3 py-1 mb-4">
                    {o.badge}
                  </span>
                )}
                <h3 className="font-display text-2xl md:text-3xl text-[#0F1F1A]">{o.name}</h3>
                {o.headline && <p className="text-[#0F1F1A] text-sm mt-1.5 mb-4">{o.headline}</p>}
                <p className="text-[#5C6B62] text-sm leading-relaxed mb-5 max-w-2xl">{o.description}</p>
                {o.features?.length > 0 && (
                  <ul className="flex flex-col gap-2">
                    {o.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 font-mono2 text-xs text-[#5C6B62]">
                        <span className="h-1.5 w-1.5 rotate-45 bg-[#C9A227] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <a
                href={withParams(`${BACKEND_URL}/api/t/${o.slug}`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`offer-cta-${o.slug}`}
                className="inline-flex items-center gap-2 border border-[#0F1F1A]/20 text-[#0F1F1A] text-xs font-semibold uppercase tracking-[0.2em] px-6 py-3.5 rounded-full group-hover:border-[#0C6B4F] group-hover:text-[#0C6B4F] hover:!bg-[#0C6B4F] hover:!text-white active:scale-[0.98] transition-[background-color,border-color,color,transform] duration-300 whitespace-nowrap"
              >
                {o.cta_label || "View Offer"}
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </FadeUp>
          ))}
        </div>
      </section>
    </div>
  );
}
